import type { Breed } from './breeds';

// Lowercase, strip accents, and turn any run of punctuation or spaces into one
// space. The spaces mark where words start, which ranking needs.
export function normalizeBreedText(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

// The same text with the separators gone, for matching: shoppers type
// "Lo-Sze" as "lo sze", "lo-sze" or "losze", and all three should find it.
function compact(normalized: string): string {
  return normalized.replace(/ /g, '');
}

// True when the query, ignoring separators, runs on from the start of a word:
// "losze" and "lo sze" both start at "Lo" in "American Lo-Sze Pugg".
function matchesAtWordStart(name: string, query: string): boolean {
  for (let i = 0; i < name.length; i += 1) {
    if (i > 0 && name[i - 1] !== ' ') continue;
    if (compact(name.slice(i)).startsWith(query)) return true;
  }
  return false;
}

// Fetch's own widget does a plain substring match in list order, so typing
// "lab" surfaces "Alabai" ahead of "Labrador". We keep the substring match (so
// nothing Fetch would find goes missing) but rank matches that start a word
// ahead of mid-word ones, keeping the source order within each group.
//
// Mixed breeds lead the unfiltered list and any search that names them
// ("mix", "large"). They are deliberately not pinned above everything: a
// mixed breed that only matches inside "Breed" or "Mixed" (say, for "re") is
// noise next to "Retriever", so it ranks with the other mid-word matches.
export function filterBreeds(breeds: Breed[], query: string): Breed[] {
  const q = compact(normalizeBreedText(query));
  if (!q) return breeds;

  const wordStart: Breed[] = [];
  const midWord: Breed[] = [];
  for (const breed of breeds) {
    const name = normalizeBreedText(breed.name);
    if (!compact(name).includes(q)) continue;
    (matchesAtWordStart(name, q) ? wordStart : midWord).push(breed);
  }
  return [...wordStart, ...midWord];
}

// A shopper who types a breed in full and tabs away has picked it; anything
// short of an exact name is not a pick, because a guessed breed means a wrong
// quote. Separators don't count, so "shihtzu" picks "Shih-Tzu".
export function findExactBreed(
  breeds: Breed[],
  query: string,
): Breed | undefined {
  const q = compact(normalizeBreedText(query));
  if (!q) return undefined;
  return breeds.find((b) => compact(normalizeBreedText(b.name)) === q);
}
